import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';

export interface MatenErrorProps {
    title?: string;
    message?: string;
    hint?: string;
    errorType?: 'error' | 'warning' | 'info';
    icon?: string;
    buttonLabel?: string;
    buttonIcon?: string;
    onButtonClick?: () => void;
    buttonLink?: string;
    showButton?: boolean;
    maxWidth?: string;
    minHeight?: string;
    testId?: string;
}

const MatenError = observer(
    ({
        title = 'Es ist ein Fehler aufgetreten',
        message = 'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
        hint,
        errorType = 'error',
        icon,
        buttonLabel = 'Erneut versuchen',
        buttonIcon = 'pi pi-refresh',
        onButtonClick,
        buttonLink,
        showButton = false,
        maxWidth = '400px',
        minHeight = '100vh',
        testId = 'maten-error',
    }: MatenErrorProps) => {
        const getErrorConfig = () => {
            switch (errorType) {
                case 'warning':
                    return {
                        backgroundColor: '#ff9800',
                        icon: icon || 'pi pi-exclamation-triangle',
                    };
                case 'info':
                    return {
                        backgroundColor: '#2196F3',
                        icon: icon || 'pi pi-info-circle',
                    };
                case 'error':
                default:
                    return {
                        backgroundColor: '#f44336',
                        icon: icon || 'pi pi-times-circle',
                    };
            }
        };

        const errorConfig = getErrorConfig();

        const handleButtonClick = () => {
            if (buttonLink) {
                window.open(buttonLink, '_blank');
            } else if (onButtonClick) {
                onButtonClick();
            }
        };

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight,
                    padding: '20px',
                }}
                data-testid={testId}
            >
                <div
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '40px 32px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        textAlign: 'center',
                        maxWidth,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            marginBottom: '24px',
                        }}
                    >
                        <div
                            style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: errorConfig.backgroundColor,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px auto',
                            }}
                        >
                            <i
                                className={errorConfig.icon}
                                style={{
                                    fontSize: '24px',
                                    color: 'white',
                                }}
                            ></i>
                        </div>
                        <h1
                            style={{
                                fontSize: '24px',
                                fontWeight: '600',
                                color: '#2c3e50',
                                margin: '0 0 16px 0',
                            }}
                            data-testid={`${testId}-title`}
                        >
                            {title}
                        </h1>
                        <p
                            style={{
                                fontSize: '16px',
                                color: '#474b4bff',
                                lineHeight: '1.5',
                                margin: '0',
                            }}
                            data-testid={`${testId}-message`}
                        >
                            {message}
                        </p>
                    </div>

                    {(showButton || buttonLink || onButtonClick) && (
                        <Button
                            label={buttonLabel}
                            icon={buttonIcon}
                            style={{
                                width: '100%',
                                backgroundColor: errorConfig.backgroundColor,
                                borderColor: errorConfig.backgroundColor,
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '16px',
                                padding: '14px 24px',
                                borderRadius: '8px',
                                border: 'none',
                                marginBottom: hint ? '16px' : '0',
                            }}
                            onClick={handleButtonClick}
                            data-testid={`${testId}-button`}
                        />
                    )}

                    {hint && (
                        <>
                            <div
                                style={{
                                    width: '100%',
                                    height: '1px',
                                    backgroundColor: '#e9ecef',
                                    margin: '20px 0 16px 0',
                                }}
                            ></div>
                            <p
                                style={{
                                    fontSize: '14px',
                                    color: '#95a5a6',
                                    margin: '0',
                                    lineHeight: '1.4',
                                }}
                            >
                                {hint}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
);

export default MatenError;
